import type { HTMLAttributes, PropsWithChildren } from 'react';
import type { To } from 'react-router-dom';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { IconName } from '../../assets/Icon/Icon';
import type { GutterContext } from '../../utilities/shared/Gutter';
import type {
  PolymorphicComponentProps,
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { IconTooltipProps, LabelTooltipProps } from '../Tooltip/Tooltip';
import { Icon } from '../../assets/Icon/Icon';
import { devices } from '../../common/devices';
import { focusRingPseudoElement } from '../../common/focus_rings';
import { sizing } from '../../common/sizing';
import { VisuallyHidden, visuallyHiddenCSS } from '../../common/visually_hidden';
import { selectors } from '../../controls/shared/styles';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, css, darkThemeSelector, fonts, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { AccessLevel } from '../../utilities/useAccessLevelContext';
import { SelectedProvider, useSelected } from '../../utilities/useSelected';
import { IconTooltip, LabelTooltip } from '../Tooltip/Tooltip';
import { useCanScrollX } from './useCanScrollX';

export const ZINDEX_DRAG_PREVIEW = 32;
export const ZINDEX_DROP_INDICATOR_ACTIVE = 31;
export const ZINDEX_DROP_INDICATOR = 30;

const ZINDEX_HEAD_LEADING = 20;
const ZINDEX_HEAD_STUCK = 19;
const ZINDEX_HEAD_SORTED = 18;
const ZINDEX_HEAD_INITIAL = 17;

const ZINDEX_BODY_STICKY_SELECTED = 16;
const ZINDEX_BODY_STICKY = 15;

const ZINDEX_BODY_SELECTED = 11;
const ZINDEX_BODY_HOVERED = 5;
const ZINDEX_BODY_INITIAL = 1;
const ZINDEX_BODY_DISABLED = 0;

const CELL_COLLAPSE_WIDTH = 32;
export const CELL_SELECTOR_WIDTH = 32;
const CELL_MAX_WIDTH_STICKY = 'min(75vw, $560)';

type TableNestedProp = boolean;
type TableMultiRowSelectionProp = boolean;
export type TableVariantProp = 'application' | 'neutral' | 'surface';

const TableNestedContext = createContext<boolean | undefined>(undefined);
export const TableNestedProvider = TableNestedContext.Provider;

const TableVariantContext = createContext<TableVariantProp | undefined>(undefined);
export const TableVariantProvider = TableVariantContext.Provider;

const TableMultiRowSelectionContext = createContext<boolean | undefined>(undefined);
export const TableMultiRowSelectionProvider = TableMultiRowSelectionContext.Provider;

export const useTableNested = (
  controlledValue?: TableNestedProp,
  defaultValue: TableNestedProp = false,
) => {
  const tableNested = useContext(TableNestedContext);
  return controlledValue ?? tableNested ?? defaultValue;
};

export const useTableVariant = (
  controlledValue?: TableVariantProp,
  defaultValue: TableVariantProp = 'application',
) => {
  const tableVariant = useContext(TableVariantContext);
  return controlledValue ?? tableVariant ?? defaultValue;
};

export const useTableMultiRowSelection = (
  controlledValue?: TableMultiRowSelectionProp,
  defaultValue: TableMultiRowSelectionProp = false,
) => {
  const tableMultiRowSelection = useContext(TableMultiRowSelectionContext);
  return controlledValue ?? tableMultiRowSelection ?? defaultValue;
};

export function TableCellEmpty({ internal }: { internal?: boolean }) {
  return (
    <Text family="monospace" internal={internal} style={{ color: 'currentColor' }}>
      -
    </Text>
  );
}

const tableValues = {
  touch: {
    head: '$28',
    base: '$36',
    subline: '$60',
    padding: '$6 $12',
  },
  mouse: {
    head: '$24',
    base: '$32',
    subline: '$48',
    padding: '$4 $10',
  },
};

const TableCellCollapsable = styled(Icon, {
  width: '$12',
  height: '$12',
});
TableCellCollapsable.displayName = 'TableCellCollapsable';

const TableCellContents = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  color: '$$textColor',
  fontFamily: fonts.sans,
  fontWeight: '$regular',
  fontVariant: 'tabular-nums',
  verticalAlign: 'middle',
  wordBreak: 'keep-all',
  whiteSpace: 'nowrap',
  cursor: 'default',
  ...BodySansSizes,

  [devices.touch]: {
    heightAll: tableValues.touch.base,
  },

  [devices.mouse]: {
    heightAll: tableValues.mouse.base,
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    hasField: {
      true: {
        [devices.touch]: {
          padding: '$6 $6',
        },

        [devices.mouse]: {
          padding: '$4 $2',
        },
      },
      false: {
        [devices.touch]: {
          padding: tableValues.touch.padding,
        },

        [devices.mouse]: {
          padding: tableValues.mouse.padding,
        },
      },
    },
    hasNested: {
      true: {
        padding: 0,
        heightAll: 'auto',
        maxHeight: 'none',

        [selectors.focus]: {
          '&::after': {
            display: 'none',
          },
        },
      },
      false: {},
    },
    hasSubline: {
      true: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',

        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },

        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    rowHasSubline: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },
        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    head: {
      true: {
        minHeight: 'auto',
      },
      false: {},
    },
    isLeading: {
      true: {
        fontWeight: '$bold',

        [devices.mouse]: {
          minWidth: 0,
        },
      },
      false: {},
    },
    isNavigable: {
      true: {
        cursor: 'pointer',

        '&::before': focusRingPseudoElement('$4', '$8'),

        [selectors.focus]: {
          '&::before': {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    internal: {
      true: {
        color: '$$internalBodyLight',

        [darkThemeSelector]: {
          color: '$$internalBodyDark',
        },
      },
      false: {},
    },
    showOnHover: {
      true: {
        [devices.mouse]: {
          transition: 'opacity 0.1s ease-in-out',

          // Only fade the cell in/out on hover when it is actually "stuck": that is, when
          // the table overflows horizontally AND has not been scrolled all the way to its
          // right edge. Once the table fits within the viewport, or the user scrolls the
          // cell into view at the right edge, the cell rests at its natural position so we
          // keep it fully visible.
          [`[data-can-scroll-x="true"]:not([data-scrolled-x-end="true"]) &`]: {
            opacity: '$$tableCellContentsOpacity',
          },
        },
      },
      false: {},
    },
    stuck: {
      top: {},
      right: {
        [devices.mouse]: {
          minWidth: 0,
        },
      },
      bottom: {},
      left: {
        [devices.mouse]: {
          minWidth: 0,
        },
      },
    },
    variant: {
      application: {},
      neutral: {},
      surface: {},
    },
  },
  compoundVariants: [
    {
      variant: 'application',
      isLeading: true,
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'top',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'right',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'bottom',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'left',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'surface',
      isLeading: true,
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'top',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'right',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'bottom',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'left',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'neutral',
      isLeading: true,
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'top',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'right',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'bottom',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'left',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      isLeading: true,
      isNavigable: true,
      css: {
        color: '$$leadingTextColor',

        [selectors.hover]: {
          color: colors.linkAlternativeInteractiveLight,

          [darkThemeSelector]: {
            color: colors.linkAlternativeInteractiveDark,
          },
        },
      },
    },
  ],
});
TableCellContents.displayName = 'TableCellContents';

const TableCellSubline = styled('div', {
  ...SmallSansSizes,
  color: '$$textColor',
  opacity: 0.7,
  marginTop: '$2',
  fontFamily: fonts.sans,
  fontWeight: '$regular',
  fontVariant: 'tabular-nums',
  wordBreak: 'break-word',
  whiteSpace: 'normal',

  variants: {
    internal: {
      true: {
        color: '$$internalBodyLight',

        [darkThemeSelector]: {
          color: '$$internalBodyDark',
        },
      },
      false: {},
    },
  },
});
TableCellSubline.displayName = 'TableCellSubline';

const TableCellMainLine = styled('div', {
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
TableCellMainLine.displayName = 'TableCellMainLine';

const TableCellContainer = styled('td', {
  position: 'relative',
  zIndex: ZINDEX_BODY_INITIAL,
  padding: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  variants: {
    collapsable: {
      true: {},
      false: {},
    },
    hasNested: {
      true: {
        padding: `0 0 $16 $${CELL_COLLAPSE_WIDTH}`,
      },
      false: {},
    },
    rowHasSubline: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },
        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    isLeading: {
      true: {
        [devices.mouse]: {
          position: 'sticky',
          left: 0,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
        },
      },
      false: {},
    },
    isSelected: {
      true: {
        zIndex: ZINDEX_BODY_SELECTED,
      },
      false: {},
    },
    stuck: {
      top: {
        [devices.mouse]: {
          position: 'sticky',
          top: 0,
        },
      },
      right: {
        [devices.mouse]: {
          position: 'sticky',
          right: 0,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
        },
      },
      bottom: {
        [devices.mouse]: {
          position: 'sticky',
          bottom: 0,
        },
      },
      left: {
        [devices.mouse]: {
          position: 'sticky',
          left: 0,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
        },
      },
    },
    isMultiRowSelection: {
      true: {},
      false: {},
    },
    hasCollapsableBefore: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      isSelected: true,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY_SELECTED,
      },
    },
    {
      isNested: false,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'top',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'right',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'bottom',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'left',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'top',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'right',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'bottom',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'left',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      stuck: 'left',
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `$${CELL_COLLAPSE_WIDTH}`,
        },
      },
    },
    {
      isLeading: true,
      isMultiRowSelection: true,
      hasCollapsableBefore: false,
      css: {
        [devices.mouse]: {
          left: `$${CELL_SELECTOR_WIDTH}`,
        },
      },
    },
    {
      isLeading: true,
      isMultiRowSelection: true,
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `$${CELL_COLLAPSE_WIDTH + CELL_SELECTOR_WIDTH}`,
        },
      },
    },
  ],
});
TableCellContainer.displayName = 'TableCellContainer';

interface TableCellProps {
  /**
   * The alignment of the cell content.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * Whether the cell should be collapsable.
   */
  collapsable?: boolean;
  /**
   * Whether the cell is collapsed.
   */
  collapsed?: boolean;
  /**
   * The number of columns the cell should span.
   */
  colSpan?: number;
  /**
   * Whether the cell has a nested `Table`.
   */
  hasNested?: boolean;
  /**
   * Whether the cell is the leading cell in the row.
   */
  isLeading?: boolean;
  /**
   * Whether the cell has a field.
   */
  hasField?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * Whether the cell is navigable and should show it can be clicked/tapped.
   */
  isNavigable?: boolean;
  /**
   * Whether the cell is stuck to the top, right, bottom, or left of the `Table`.
   */
  stuck?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Access level for the cell.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Whether the cell is visible only to Meter employees.
   */
  internal?: boolean;
  /**
   * The group the cell belongs to.
   */
  group?: string | null;
  /**
   * Hotkey navigation targets for the cell.
   */
  hotkeyTos?: Record<string, To>;
  /**
   * Navigation target for the cell; also marks the cell navigable.
   */
  to?: To;
  /**
   * Whether there is a collapsable cell before this cell (used for sticky positioning).
   */
  hasCollapsableBefore?: boolean;
  /**
   * Subline content to display below the main content. When provided, the cell will display
   * two rows: the main content and the subline content.
   */
  subline?: React.ReactNode;
  /**
   * Whether any cell in this row has a subline. When true, this cell will match the height
   * of cells with sublines even if it doesn't have one itself.
   */
  rowHasSubline?: boolean;
  /**
   * Whether the cell content should be hidden until the row is hovered.
   */
  showOnHover?: boolean;
}

function TableCellPassthrough<Tag extends React.ElementType>(
  {
    as,
    alignment,
    children,
    collapsed,
    collapsable,
    colSpan,
    hasField = false,
    hasNested,
    isLeading,
    isNavigable,
    isNested,
    isSelected,
    multiRowSelection,
    stuck,
    to,
    internal,
    accessLevel,
    group,
    hotkeyTos,
    hasCollapsableBefore,
    subline,
    rowHasSubline,
    style,
    ...props
  }: PolymorphicComponentProps<Tag, TableCellProps>,
  ref: PolymorphicRef<Tag>,
) {
  const tableVariant = useTableVariant();
  const tableNested = useTableNested(isNested);
  const tableMultiRowSelection = useTableMultiRowSelection(multiRowSelection);
  const tableRowSelected = useSelected(isSelected);
  const hasSubline = !!subline;
  const internalValue =
    internal ?? (accessLevel ? accessLevel === AccessLevel.Internal : undefined);

  return (
    <TableCellContainer
      collapsable={collapsable}
      colSpan={colSpan}
      hasNested={hasNested}
      isNested={tableNested}
      isMultiRowSelection={tableMultiRowSelection}
      isLeading={isLeading}
      isSelected={tableRowSelected}
      stuck={stuck}
      data-stuck={stuck}
      hasCollapsableBefore={hasCollapsableBefore}
      rowHasSubline={rowHasSubline}
      style={style}
    >
      <TableCellContents
        as={collapsable ? 'button' : as}
        to={to}
        {...(as && typeof as !== 'string' ? { hotkeyTos } : {})}
        alignment={alignment}
        hasField={hasField}
        hasNested={hasNested}
        hasSubline={hasSubline}
        rowHasSubline={rowHasSubline}
        isLeading={isLeading}
        isNavigable={!!to || isNavigable || collapsable}
        isNested={tableNested}
        stuck={stuck}
        tabIndex={collapsable || (isLeading && to) ? 0 : undefined}
        aria-expanded={collapsable ? !collapsed : undefined}
        data-has-nested={hasNested && 'true'}
        {...props}
        internal={internalValue}
        variant={tableVariant}
        ref={ref}
      >
        {collapsable && (
          <>
            <TableCellCollapsable icon={collapsed ? 'chevron-right' : 'chevron-down'} />
            {/* The chevron is this cell's only content, so nothing else names the button. Static
                text, not expand/collapse: `aria-expanded` already carries the state. */}
            <VisuallyHidden>Toggle row</VisuallyHidden>
          </>
        )}
        {hasSubline ? (
          <>
            <TableCellMainLine>{children}</TableCellMainLine>
            <TableCellSubline internal={internalValue}>{subline}</TableCellSubline>
          </>
        ) : (
          children
        )}
      </TableCellContents>
    </TableCellContainer>
  );
}

export const TableCell = React.forwardRef(TableCellPassthrough) as <
  Tag extends React.ElementType = 'div',
>(
  props: PolymorphicComponentPropsWithRef<Tag, TableCellProps>,
) => ReturnType<typeof TableCellPassthrough>;

const stateCellWidth = 8;
const bufferCellWidth = (context: GutterContext = 'application') => {
  if (context === 'section') {
    return sizing.tertiary - stateCellWidth;
  }
  if (context === 'content') {
    return sizing.secondary - stateCellWidth;
  }
  return sizing.primary - stateCellWidth;
};

export const TableCellBufferContainer = styled('td', {
  variants: {
    head: {
      true: {
        position: 'sticky',
        top: 0,
        zIndex: ZINDEX_HEAD_INITIAL,

        [devices.touch]: {
          heightAll: tableValues.touch.head,
        },

        [devices.mouse]: {
          heightAll: tableValues.mouse.head,
        },
      },
      false: {},
    },
    context: {
      application: {
        widthAll: `$${bufferCellWidth('application')}`,
      },
      content: {
        widthAll: `$${bufferCellWidth('content')}`,
      },
      section: {
        widthAll: `$${bufferCellWidth('section')}`,
      },
    },
    isNested: {
      true: {},
      false: {},
    },
    side: {
      leading: {},
      trailing: {},
    },
    variant: {
      application: {},
      neutral: {},
      surface: {},
    },
  },

  compoundVariants: [
    {
      head: true,
      variant: 'application',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgApplicationLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgApplicationDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'application',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgApplicationLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgApplicationDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'surface',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgSurfaceLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgSurfaceDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'surface',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgSurfaceLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgSurfaceDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgNeutralLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgNeutralDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgNeutralLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgNeutralDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      isNested: true,
      side: 'leading',
      css: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },
      },
    },
  ],
});
TableCellBufferContainer.displayName = 'TableCellBufferContainer';

type TableCellBufferProps = {
  /**
   * The context of the cell.
   */
  context?: GutterContext;
  /**
   * Whether the cell state is at the top of the table.
   */
  head?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * The side of row the cell appears.
   */
  side?: 'leading' | 'trailing';
};

export function TableCellBuffer({
  context = 'application',
  head,
  isNested,
  side,
  ...remaining
}: TableCellBufferProps) {
  const tableNested = useTableNested(isNested);
  const tableVariant = useTableVariant();
  return (
    <TableCellBufferContainer
      context={context}
      head={head}
      isNested={tableNested}
      side={side}
      variant={tableVariant}
      {...remaining}
    />
  );
}

const TableCellStateContents = styled('div', {
  width: '100%',

  [devices.touch]: {
    heightAll: tableValues.touch.base,
  },

  [devices.mouse]: {
    heightAll: tableValues.mouse.base,
  },

  variants: {
    head: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.head,
        },

        [devices.mouse]: {
          heightAll: tableValues.mouse.head,
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    rowHasSubline: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },
        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    side: {
      leading: {
        borderRadius: '$10 0 0 $10',
      },
      trailing: {
        borderRadius: '0 $10 $10 0',
      },
    },
  },
  compoundVariants: [
    {
      head: true,
      css: {
        borderRadius: 0,
      },
    },
    {
      head: true,
      isNested: true,
      side: 'leading',
      css: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },
      },
    },
  ],
});
TableCellStateContents.displayName = 'TableCellStateContents';

const TableCellStateContainer = styled('td', {
  padding: 0,
  width: `$${stateCellWidth}`,
  minWidth: `$${stateCellWidth}`,
  maxWidth: `$${stateCellWidth}`,

  variants: {
    head: {
      true: {
        position: 'sticky',
        top: 0,
        zIndex: ZINDEX_HEAD_INITIAL,
      },
      false: {},
    },
    variant: {
      application: {},
      neutral: {},
      surface: {},
    },
  },
  compoundVariants: [
    {
      head: true,
      variant: 'application',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      head: true,
      variant: 'surface',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
  ],
});
TableCellStateContainer.displayName = 'TableCellStateContainer';

type TableCellStateProps = {
  /**
   * Whether the cell state is at the top of the table.
   */
  head?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * The side of row the cell appears.
   */
  side?: 'leading' | 'trailing';
  /**
   * Whether any cell in this row has a subline. When true, this cell will match the height
   * of cells with sublines even if it doesn't have one itself.
   */
  rowHasSubline?: boolean;
};

export function TableCellState<Tag extends React.ElementType>({
  head,
  isNested,
  side,
  rowHasSubline,
  ...remaining
}: PolymorphicComponentProps<Tag, TableCellStateProps>) {
  const tableVariant = useTableVariant();
  const tableNested = useTableNested(isNested);
  return (
    <TableCellStateContainer head={head} data-side={side} variant={tableVariant} {...remaining}>
      <TableCellStateContents
        head={head}
        isNested={tableNested}
        side={side}
        rowHasSubline={rowHasSubline}
      />
    </TableCellStateContainer>
  );
}

const TableRowStyles = css({
  $$iconColor: colors.iconNeutralLight,
  $$textColor: colors.bodyNeutralLight,
  $$leadingTextColor: colors.linkAlternativeInitialLight,

  $$internalBodyLight: colors.internalBodyLight,
  $$internalBodyDark: colors.internalBodyDark,

  position: 'relative',
  width: '100%',

  $$tableCellContentsOpacity: 0,

  [darkThemeSelector]: {
    $$iconColor: colors.iconNeutralDark,
    $$textColor: colors.bodyNeutralDark,
    $$leadingTextColor: colors.linkAlternativeInitialDark,
  },

  [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
    strokeTopBottom: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeTopBottom: colors.strokeNeutralDark,
    },
  },

  [selectors.hover]: {
    $$tableCellContentsOpacity: 1,
  },

  // Keep showOnHover cells (e.g. actions) visible while a row dropdown is open — the
  // pointer often leaves the row when interacting with the menu.
  '&:has([data-state="open"])': {
    $$tableCellContentsOpacity: 1,
  },
});

const TableRowContainer = styled('tr', TableRowStyles, {
  position: 'relative',
  zIndex: ZINDEX_BODY_INITIAL,

  [selectors.hover]: {
    zIndex: ZINDEX_BODY_HOVERED,
  },

  '&:has([data-state="open"])': {
    zIndex: ZINDEX_BODY_HOVERED,
  },

  variants: {
    isDisabled: {
      true: {
        zIndex: ZINDEX_BODY_DISABLED,
        $$iconColor: colors.bodyNeutralLight,
        $$textColor: colors.bodyNeutralLight,
        $$leadingTextColor: colors.bodyNeutralLight,

        opacity: 0.5,

        [darkThemeSelector]: {
          $$iconColor: colors.bodyNeutralDark,
          $$textColor: colors.bodyNeutralDark,
          $$leadingTextColor: colors.bodyNeutralDark,
        },

        [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
          background: colors.bgApplicationLight,
          strokeTopBottom: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.bgApplicationDark,
            strokeTopBottom: colors.strokeNeutralDark,
          },
        },

        [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
          background: colors.bgApplicationLight,

          [darkThemeSelector]: {
            background: colors.bgApplicationDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
          strokeNoRight: colors.bgApplicationLight,

          [darkThemeSelector]: {
            strokeNoRight: colors.bgApplicationDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
          strokeNoLeft: colors.bgApplicationLight,

          [darkThemeSelector]: {
            strokeNoLeft: colors.bgApplicationDark,
          },
        },

        [selectors.hover]: {
          zIndex: ZINDEX_BODY_DISABLED,

          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            $$leadingTextColor: colors.bodyNeutralLight,
            background: colors.bgNeutralLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              $$leadingTextColor: colors.bodyNeutralDark,
              background: colors.bgNeutralDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
    },
    isDragging: {
      true: {
        opacity: 0.8,
      },
      false: {},
    },
    isSelected: {
      true: {
        zIndex: ZINDEX_BODY_SELECTED,
        $$iconColor: colors.bodyBrandLight,
        $$textColor: colors.headingBrandLight,
        $$leadingTextColor: colors.headingBrandLight,

        [darkThemeSelector]: {
          $$iconColor: colors.bodyBrandDark,
          $$textColor: colors.headingBrandDark,
          $$leadingTextColor: colors.headingBrandDark,
        },

        [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
          background: colors.bgBrandLight,
          strokeTopBottom: colors.strokeBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeTopBottom: colors.strokeBrandDark,
          },
        },

        [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
          background: colors.bgBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
          strokeNoRight: colors.strokeBrandLight,

          [darkThemeSelector]: {
            strokeNoRight: colors.strokeBrandDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
          strokeNoLeft: colors.strokeBrandLight,

          [darkThemeSelector]: {
            strokeNoLeft: colors.strokeBrandDark,
          },
        },

        [selectors.hover]: {
          zIndex: ZINDEX_BODY_SELECTED,

          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            $$leadingTextColor: colors.headingBrandLight,
            background: colors.bgBrandLight,
            strokeTopBottom: colors.strokeBrandLight,

            [darkThemeSelector]: {
              $$leadingTextColor: colors.headingBrandDark,
              background: colors.bgBrandDark,
              strokeTopBottom: colors.strokeBrandDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            background: colors.bgBrandLight,

            [darkThemeSelector]: {
              background: colors.bgBrandDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            strokeNoRight: colors.strokeBrandLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeBrandDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            strokeNoLeft: colors.strokeBrandLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeBrandDark,
            },
          },
        },
      },
      false: {},
    },
    variant: {
      application: {
        [selectors.hover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgNeutralLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
      surface: {
        [selectors.hover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgSurfaceLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgSurfaceDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgSurfaceLight,

            [darkThemeSelector]: {
              background: colors.bgSurfaceDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
      neutral: {
        [selectors.hover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgApplicationLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgApplicationDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgApplicationLight,

            [darkThemeSelector]: {
              background: colors.bgApplicationDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
    },
  },
});
TableRowContainer.displayName = 'TableRowContainer';

interface TableRowProps {
  /**
   * The content of the row.
   */
  children?: React.ReactNode | undefined;
  /**
   * Whether the row is disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the row is being dragged.
   */
  isDragging?: boolean;
  /**
   * Whether the row shows a hover state. Defaults to true.
   */
  isHoverable?: boolean;
  /**
   * Whether the row is navigable and should show it can be clicked/tapped.
   */
  isNavigable?: boolean;
  /**
   * Whether the row is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * Whether the row is selected.
   */
  isSelected?: boolean;
}

function TableRowPassthrough<Tag extends React.ElementType>(
  {
    isDragging,
    isHoverable,
    isNested,
    isSelected,
    ...props
  }: PolymorphicComponentProps<Tag, TableRowProps>,
  ref: PolymorphicRef<Tag>,
) {
  const tableVariant = useTableVariant();
  return (
    <TableRowContainer
      ref={ref}
      {...props}
      isDragging={isDragging}
      isSelected={isSelected}
      variant={isHoverable !== false ? tableVariant : undefined}
      data-is-selected={isSelected && 'true'}
    >
      <SelectedProvider value={Boolean(isSelected)}>{props.children}</SelectedProvider>
    </TableRowContainer>
  );
}

export const TableRow = React.forwardRef(TableRowPassthrough) as <
  Tag extends React.ElementType = 'tr',
>(
  props: PolymorphicComponentPropsWithRef<Tag, TableRowProps>,
) => ReturnType<typeof TableRowPassthrough>;

export const TableBody = styled('tbody');
TableBody.displayName = 'TableBody';

const TableHeadCellSort = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  size: 0,

  variants: {
    isVisible: {
      true: {
        // TRICKY: Toggle visibility rather than display to always reserve
        // space in the layout for the icon.
        visibility: 'visible',
      },
      false: {
        visibility: 'hidden',
      },
    },
  },
  defaultVariants: {
    isVisible: false,
  },
});
TableHeadCellSort.displayName = 'TableHeadCellSort';

const TableHeadCellIcon = styled(Icon, {
  size: '$12',
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },
});
TableHeadCellIcon.displayName = 'TableHeadCellIcon';

const TableHeadCellLabel = styled(Text, {
  variants: {
    visible: {
      true: {},
      false: {
        ...visuallyHiddenCSS,
      },
    },
  },
});
TableHeadCellLabel.displayName = 'TableHeadCellLabel';

// Holds an icon and the label it stands in for. `display: contents` so the pair lays out exactly as
// the lone icon used to; it is an element rather than a fragment so a tooltip can still wrap it.
const TableHeadCellIconLabel = styled('div', {
  display: 'contents',
});
TableHeadCellIconLabel.displayName = 'TableHeadCellIconLabel';

const TableHeadCellSortIcon = styled(Icon, {
  display: 'flex',
  size: '$8',
  marginLeft: '$2',
});
TableHeadCellSortIcon.displayName = 'TableHeadCellSortIcon';

const TableHeadCellBadge = styled('div', {
  marginLeft: '$4',
});
TableHeadCellBadge.displayName = 'TableHeadCellBadge';

const TableHeadCellContents = styled('div', {
  ...SmallSansSizes,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  strokeTopBottom: colors.strokeNeutralLight,
  cursor: 'pointer',
  fontWeight: '$bold',
  fontFamily: fonts.sans,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',

  [darkThemeSelector]: {
    strokeTopBottom: colors.strokeNeutralDark,
  },

  [devices.touch]: {
    heightAll: tableValues.touch.head,
    padding: tableValues.touch.padding,
  },

  [devices.mouse]: {
    heightAll: tableValues.mouse.head,
    padding: tableValues.mouse.padding,
  },

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    canSort: {
      true: {
        outline: 'none',
        cursor: 'pointer',

        // No `position: relative` here on purpose: the ring lays out against the cell, not the
        // contents, and adding one would move it.
        '&::before': focusRingPseudoElement('$4', '$8'),

        [selectors.focus]: {
          '&::before': {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {
        cursor: 'default',
      },
    },
    collapsable: {
      true: {
        widthAll: '100%',
      },
      false: {},
    },
    internal: {
      true: {
        color: colors.internalBodyLight,

        [darkThemeSelector]: {
          color: colors.internalBodyDark,
        },
      },
      false: {},
    },
    variant: {
      application: {
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgApplicationDark,
        },
      },
      surface: {
        backgroundColor: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgSurfaceDark,
        },
      },
      neutral: {
        backgroundColor: colors.bgNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgNeutralDark,
        },
      },
    },
  },
});
TableHeadCellContents.displayName = 'TableHeadCellContents';

const TableHeadCellContainer = styled('th', {
  position: 'sticky',
  top: 0,
  zIndex: ZINDEX_HEAD_INITIAL,
  padding: 0,
  userSelect: 'none',

  variants: {
    collapsable: {
      true: {
        width: `$${CELL_COLLAPSE_WIDTH}`,
      },
      false: {},
    },
    isLeading: {
      true: {
        [devices.mouse]: {
          left: 0,
          zIndex: ZINDEX_HEAD_LEADING,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
          overflow: 'hidden',
        },
      },
      false: {},
    },
    isMultiRowSelection: {
      true: {},
      false: {},
    },
    hasCollapsableBefore: {
      true: {},
      false: {},
    },
    isSorted: {
      true: {
        zIndex: ZINDEX_HEAD_SORTED,
      },
      false: {},
    },
    stuck: {
      top: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
        },
      },
      right: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
          right: 0,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
          overflow: 'hidden',
        },
      },
      bottom: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
          bottom: 0,
        },
      },
      left: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
          left: 0,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
          overflow: 'hidden',
        },
      },
    },
  },
  compoundVariants: [
    {
      stuck: 'left',
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `$${CELL_COLLAPSE_WIDTH}`,
        },
      },
    },
    {
      isMultiRowSelection: true,
      isLeading: true,
      hasCollapsableBefore: false,
      css: {
        [devices.mouse]: {
          left: `$${CELL_SELECTOR_WIDTH}`,
        },
      },
    },
    {
      isMultiRowSelection: true,
      isLeading: true,
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `$${CELL_COLLAPSE_WIDTH + CELL_SELECTOR_WIDTH}`,
        },
      },
    },
  ],
});
TableHeadCellContainer.displayName = 'TableHeadCellContainer';

export type TableHeadCellTooltipProp = { label?: LabelTooltipProps; icon?: IconTooltipProps };

type TableHeadCellProps = PropsWithChildren<
  HTMLAttributes<HTMLTableCellElement> & {
    /**
     * The alignment of the cell content.
     */
    alignment?: 'start' | 'center' | 'end';
    /**
     * Whether the cell is collapsable.
     */
    collapsable?: boolean;
    /**
     * Whether the cell is collapsed.
     */
    collapsed?: boolean;
    /**
     * Whether to hide the label.
     */
    hideLabel?: boolean;
    /**
     * Whether to hide the sort icon.
     */
    hideSortIcon?: boolean;
    /**
     * The icon to display in the cell, label is hidden.
     */
    icon?: IconName;
    /**
     * Access level of the cell.
     */
    accessLevel?: AccessLevelProp | null;
    /**
     * Whether the cell is visible only to Meter employees.
     */
    internal?: boolean;
    /**
     * Whether the cell is part of a nested `Table`.
     */
    isNested?: boolean;
    /**
     * Whether the cell is leading.
     */
    isLeading?: boolean;
    /**
     * The sort direction of the cell.
     */
    sortDirection?: 'asc' | 'desc' | boolean;
    /**
     * Whether the cell is stuck to the top, right, bottom, or left of the `Table`.
     */
    stuck?: 'top' | 'right' | 'bottom' | 'left';
    /**
     * Determines both the type and contents of the tooltip to display when the cell is hovered.
     */
    tooltip?: TableHeadCellTooltipProp;
    /**
     * Whether the cell has a field.
     */
    hasField?: boolean;
    /**
     * The group the cell belongs to.
     */
    group?: string | null;
    /**
     * Whether there is a collapsable cell before this cell (used for sticky positioning).
     */
    hasCollapsableBefore?: boolean;
  }
>;

function TableHeadCellInner(
  {
    alignment,
    children,
    collapsable,
    collapsed,
    hideLabel = false,
    hideSortIcon,
    icon,
    accessLevel,
    internal,
    isNested,
    isLeading,
    sortDirection,
    stuck,
    tooltip,
    hasField,
    group,
    hasCollapsableBefore,
    ...props
  }: TableHeadCellProps,
  ref: React.ForwardedRef<HTMLTableCellElement>,
) {
  const tableVariant = useTableVariant();
  const isMultiRowSelection = useTableMultiRowSelection();
  if (accessLevel === null) return null;

  const hasHeaderText = children != null && children !== false && children !== '';

  const tableHeadCellLabelContent = hideLabel ? (
    <TableHeadCellLabel visible={false}>{children}</TableHeadCellLabel>
  ) : (
    children
  );

  // `icon` stands in for the visible label, so the label still has to exist somewhere a reader can
  // get at it — otherwise the column heads its cells with a picture and nothing else.
  const tableHeadCellLabel = icon ? (
    <TableHeadCellIconLabel>
      <TableHeadCellIcon icon={icon} />
      {hasHeaderText && <TableHeadCellLabel visible={false}>{children}</TableHeadCellLabel>}
    </TableHeadCellIconLabel>
  ) : (
    tableHeadCellLabelContent
  );

  let tableHeadCellContents = tableHeadCellLabel;

  if (tooltip && tooltip.label) {
    tableHeadCellContents = (
      <LabelTooltip
        preset="small"
        weight="bold"
        internal={internal || accessLevel === AccessLevel.Internal}
        {...tooltip.label}
      >
        {tableHeadCellLabel}
      </LabelTooltip>
    );
  }

  if (tooltip && tooltip.icon) {
    tableHeadCellContents = (
      <IconTooltip
        size={16}
        internal={internal || accessLevel === AccessLevel.Internal}
        {...tooltip.icon}
      />
    );
  }

  const hasSort = !collapsable && (!hideSortIcon || !!sortDirection);

  // A head cell with nothing in it heads nothing. Readers announce the column header as they move
  // between cells, so an empty `<th>` leaves every cell in the column advertised as having a header
  // and unable to say what it is — better to not claim one. Head-row spacers already do this:
  // `TableCellBuffer` and `TableCellState` are `<td>` even with `head`. A `collapsable` cell is
  // excluded because it is itself the expand-all button, which is named below.
  const isEmpty = !collapsable && !tooltip && !hasHeaderText;

  return (
    <TableHeadCellContainer
      as={isEmpty ? 'td' : undefined}
      collapsable={collapsable}
      isLeading={isLeading}
      isSorted={!!sortDirection}
      isMultiRowSelection={isMultiRowSelection}
      stuck={stuck}
      hasCollapsableBefore={hasCollapsableBefore}
      {...props}
      ref={ref}
    >
      <TableHeadCellContents
        as={collapsable ? 'button' : 'div'}
        collapsable={collapsable}
        canSort={!hideSortIcon || !!sortDirection}
        tabIndex={collapsable || !hideSortIcon ? 0 : undefined}
        aria-expanded={collapsable ? !collapsed : undefined}
        alignment={alignment}
        internal={internal || accessLevel === AccessLevel.Internal}
        variant={tableVariant}
      >
        <AlignStack
          align="center"
          preset="small"
          width="min-content"
          start={
            collapsable && (
              <>
                <TableCellCollapsable icon={collapsed ? 'chevron-right' : 'chevron-down'} />
                {!hasHeaderText && <VisuallyHidden>Toggle all rows</VisuallyHidden>}
              </>
            )
          }
          end={
            (accessLevel && accessLevel !== 'public') || hasSort ? (
              <>
                {accessLevel && accessLevel !== 'public' && (
                  <TableHeadCellBadge>
                    <FeatureBadge size="x-small" type={accessLevel} />
                  </TableHeadCellBadge>
                )}
                {hasSort && (
                  <TableHeadCellSort isVisible={!!sortDirection}>
                    <TableHeadCellSortIcon
                      icon={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
                      size={10}
                    />
                  </TableHeadCellSort>
                )}
              </>
            ) : null
          }
        >
          {tableHeadCellContents}
        </AlignStack>
      </TableHeadCellContents>
    </TableHeadCellContainer>
  );
}

export const TableHeadCell = React.forwardRef(TableHeadCellInner);

const TableHeadRowContainer = styled('tr', {
  position: 'relative',
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});
TableHeadRowContainer.displayName = 'TableHeadRowContainer';

type TableHeadRowProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

function TableHeadRowPassthrough(
  props: TableHeadRowProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => setIsPinned(e.intersectionRatio < 1), {
      threshold: [1],
    });

    if (innerRef.current) {
      observer.observe(innerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <TableHeadRowContainer {...props} ref={mergeRefs([ref, innerRef])} data-is-pinned={isPinned} />
  );
}

export const TableHeadRow = React.forwardRef<HTMLDivElement, TableHeadCellProps>(
  TableHeadRowPassthrough,
);

export const TableHead = styled('thead');
TableHead.displayName = 'TableHead';

const TableContainer = styled('table', {
  position: 'relative',
  width: '100%',

  variants: {
    isNested: {
      true: {},
      false: {},
    },
    multiRowSelection: {
      true: {},
      false: {},
    },
  },
});
TableContainer.displayName = 'TableContainer';

interface TableProps extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  /**
   * Whether the table is nested within a parent `Table`.
   */
  isNested?: TableNestedProp;
  /**
   * Whether the table allows for multi-row selection.
   */
  multiRowSelection?: boolean;
  /**
   * The variant of table.
   */
  variant?: TableVariantProp;
}

export const Table = React.forwardRef<HTMLDivElement, TableProps>(
  ({ children, isNested, multiRowSelection, variant, ...props }, ref) => {
    const [scrollRef, canScrollX, isScrolledToEnd] = useCanScrollX();
    return (
      <TableContainer
        ref={mergeRefs([ref, scrollRef])}
        isNested={isNested}
        data-can-scroll-x={canScrollX}
        data-scrolled-x-end={isScrolledToEnd}
        {...props}
      >
        <TableMultiRowSelectionProvider value={multiRowSelection}>
          <TableNestedProvider value={isNested}>
            <TableVariantProvider value={variant}>{children}</TableVariantProvider>
          </TableNestedProvider>
        </TableMultiRowSelectionProvider>
      </TableContainer>
    );
  },
);
